import pMap from 'p-map';

import { supabase } from '@/lib/supabase';
import type { Course, CourseUser } from '@/lib/types';
import { uniqueByCourseId } from '@/lib/utils';

const schoolId = import.meta.env.VITE_SCHOOL_ID;
const CONCURRENT_PROMISES = 3;

export interface UpdateUserOptions {
	userCourses: Pick<Course, 'course_id' | 'course_name'>[] | undefined;
	currentUserCourses: CourseUser[];
	userId: string;
	firstName: string;
	lastName: string;
	role: string;
	deptChair: string; // Firebase UID
}

/** Diff selected courses against existing assignments, collapsing duplicate rows. */
export function diffCourseAssignments(
	currentUserCourses: CourseUser[],
	userCourses: Pick<Course, 'course_id' | 'course_name'>[] | undefined
): { toDeleteIds: string[]; toAdd: Pick<Course, 'course_id' | 'course_name'>[] } {
	const selected = uniqueByCourseId(userCourses ?? []).filter((c) => c.course_id);
	const selectedIds = new Set(selected.map((c) => c.course_id));

	const keptCourseIds = new Set<string>();
	const toDeleteIds: string[] = [];

	for (const assignment of currentUserCourses) {
		if (!assignment.courses_users_id) continue;
		if (
			!assignment.course_id ||
			!selectedIds.has(assignment.course_id) ||
			keptCourseIds.has(assignment.course_id)
		) {
			toDeleteIds.push(assignment.courses_users_id);
			continue;
		}
		keptCourseIds.add(assignment.course_id);
	}

	return {
		toDeleteIds,
		toAdd: selected.filter((c) => !keptCourseIds.has(c.course_id)),
	};
}

export const updateUser = async (variables: UpdateUserOptions) => {
	const { currentUserCourses, userCourses, userId, firstName, lastName, role, deptChair } =
		variables;

	const { toDeleteIds, toAdd } = diffCourseAssignments(currentUserCourses, userCourses);

	if (toDeleteIds.length > 0) {
		await pMap(
			toDeleteIds,
			async (coursesUsersId) => {
				const { error } = await supabase
					.from('courses_users')
					.delete()
					.eq('courses_users_id', coursesUsersId);

				if (error) throw error;
			},
			{ concurrency: CONCURRENT_PROMISES }
		);
	}

	if (toAdd.length > 0) {
		await pMap(
			toAdd,
			async (course) => {
				if (course.course_id) {
					const { error } = await supabase.from('courses_users').insert({
						course_id: course.course_id,
						user_id: userId,
					});

					if (error) throw error;
				}
			},
			{ concurrency: CONCURRENT_PROMISES }
		);
	}

	// Update user information
	// Note: user_dept_chair stores Firebase UIDs, not UUIDs
	const updateData: {
		user_first: string;
		user_last: string;
		user_role: string;
		user_dept_chair: string | null;
	} = {
		user_first: firstName,
		user_last: lastName,
		user_role: role,
		user_dept_chair: deptChair || null,
	};

	const { data, error } = await supabase
		.from('users')
		.update(updateData)
		.eq('user_id', userId)
		.eq('school_id', schoolId)
		.select('user_id, user_dept_chair')
		.single();

	if (error) throw error;

	return { affected_rows: 1, returning: { user_dept_chair: data.user_dept_chair } };
};
